import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Col, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { MdDeleteOutline, MdEdit } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CustomModal from '../../components/CustomModal';
import Input from '../../components/input';
import MultiLineText from '../../components/MultiLineText';
import PaginationCustom from '../../components/Pagination';
import Select from '../../components/Select';
import { createServiceLineItem, deleteServiceLineItem, fetchCategoriesDropdown, fetchNextServiceId, fetchPartsList, fetchReasonsDropdown, fetchServicesReminder, updateServiceLineItem } from '../../store/Slices/ServiceSlice';
import { clearWarehousesWithPartStock, getWarehousesWithPartStock } from '../../store/Slices/PartVendorSlice';
import { dateMonthYear } from '../../utils/helper';
import { serviceLineSchema } from '../../utils/Schema';
import { lastCompleteIcon, nextDueIcon, saveLineItemIcon, searchIcon, serviceLineItemIcon } from '../../utils/svgs';
import { errorNotify, successNotify } from '../../utils/toast';

const ServiceLineItem = ({
    vehicleDetailLoading,
    vehicleDetail,
    selectedVehicle,
    assetType,
    assetId,
    assetLabel,
    refreshVehicleDetail
}) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [showAddItemBtn, setShowAddItemBtn] = useState(false);
    const [selectServiceItem, setSelectServiceItem] = useState(null);
    const [showServiceReminder, setShowServiceReminder] = useState(false)
    const [currentPage, setCurrentPage] = useState(1);
    const [editLineItem, setEditLineItem] = useState(null);
    const [reservedServiceId, setReservedServiceId] = useState(() => {
        const parsedRouteId = Number(id);
        return Number.isFinite(parsedRouteId) ? parsedRouteId : null;
    });

    const { categoriesLoading, categoriesData, reasonsLoading, reasonsData, partsLoading, partsData, createLineItemLoading, getServicesReminderLoading, getServicesReminderPaginated, nextServiceId } = useSelector((state) => state.services);
    const { warehousesWithPartStockData, getWarehousesWithPartStockLoading } = useSelector((state) => state.partVendors);

    const stripHtmlTags = (value) =>
        String(value || '')
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();

    const getPartDisplayName = (part) => {
        const byName = stripHtmlTags(part?.name);
        if (byName) return byName;

        const byDescription = stripHtmlTags(part?.description);
        if (byDescription) return byDescription;

        const byPartNumber = stripHtmlTags(part?.part_number);
        if (byPartNumber) return byPartNumber;

        return 'Unknown Part';
    };

    const getLineItemDisplayName = (item) => {
        if (!item) return 'Other';
        if (item?.part) return getPartDisplayName(item.part);
        return getPartDisplayName(item);
    };

    const getPartIdForWarehouseApi = (partItem) => {
        const candidates = [
            partItem?.part_id,
            partItem?.part?.id,
            partItem?.part?.part_id,
            partItem?.id
        ];

        for (const candidate of candidates) {
            if (candidate === undefined || candidate === null || candidate === '') continue;
            if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;

            const raw = String(candidate).trim();
            if (!raw) continue;
            if (/^\d+$/.test(raw)) return Number(raw);

            const match = raw.match(/(\d+)/);
            if (match?.[1]) return Number(match[1]);
        }

        return null;
    };

    const getWarehouseIdFromItem = (item) => {
        const candidates = [
            item?.warehouse_id,
            item?.warehouse?.id,
            item?.warehouse?.warehouse_id,
            item?.warehouse?.value,
            item?.part?.warehouse_id
        ];

        for (const candidate of candidates) {
            if (candidate === undefined || candidate === null || candidate === '') continue;
            if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;

            const raw = String(candidate).trim();
            if (!raw) continue;
            if (/^\d+$/.test(raw)) return Number(raw);

            const match = raw.match(/(\d+)/);
            if (match?.[1]) return Number(match[1]);
        }

        return '';
    };

    const getWarehouseLabelFromItem = (item) => (
        item?.warehouse?.label ||
        item?.warehouse?.name ||
        item?.warehouse_name ||
        ''
    );

    const getNormalizedNumericValue = (value) => {
        if (value === undefined || value === null || value === '') {
            return null;
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        const raw = String(value).trim();
        if (!raw) {
            return null;
        }

        if (/^\d+$/.test(raw)) {
            return Number(raw);
        }

        const match = raw.match(/(\d+)/);
        return match?.[1] ? Number(match[1]) : null;
    };
    const normalizedAssetType = String(assetType || 'vehicle').trim().toLowerCase();
    const isEquipmentAsset = normalizedAssetType === 'equipment';
    const isTyreAsset = normalizedAssetType === 'tyre';
    const displayAssetLabel = assetLabel || (isEquipmentAsset ? 'Equipment' : isTyreAsset ? 'Tyre' : 'Vehicle');

    const getExistingServiceId = (item) => {
        const candidates = [
            item?.service_id,
            item?.service?.id,
            item?.service?.service_id,
            item?.maintenances_service_id
        ];

        for (const candidate of candidates) {
            const normalizedValue = getNormalizedNumericValue(candidate);
            if (normalizedValue !== null) {
                return normalizedValue;
            }
        }

        return null;
    };

    const getResolvedAssetId = (fallbackVehicleId) => {
        const candidates = [
            assetId,
            fallbackVehicleId,
            selectedVehicle?.value,
            vehicleDetail?.asset_id,
            vehicleDetail?.id
        ];

        for (const candidate of candidates) {
            const normalizedValue = getNormalizedNumericValue(candidate);
            if (normalizedValue !== null) {
                return normalizedValue;
            }
        }

        return null;
    };

    const getCurrentServiceLineItems = () => {
        if (Array.isArray(vehicleDetail?.asset_service_line_items)) {
            return vehicleDetail.asset_service_line_items;
        }

        if (Array.isArray(vehicleDetail?.service_line_items)) {
            return vehicleDetail.service_line_items;
        }

        return [];
    };

    const getContextVehicleId = () => {
        const candidates = normalizedAssetType === 'vehicle'
            ? [
                vehicleDetail?.id,
                vehicleDetail?.vehicle_id,
                selectedVehicle?.value
            ]
            : [
                vehicleDetail?.vehicle_id,
                vehicleDetail?.vehicle?.id,
                vehicleDetail?.vehicle?.vehicle_id
            ];

        for (const candidate of candidates) {
            const normalizedValue = getNormalizedNumericValue(candidate);
            if (normalizedValue !== null) {
                return normalizedValue;
            }
        }

        return null;
    };

    const getContextVehicleTypeId = () => {
        const candidates = [
            vehicleDetail?.vehicle_type_id,
            vehicleDetail?.vehicle?.vehicle_type_id,
            vehicleDetail?.vehicle?.type_id,
            vehicleDetail?.vehicle_type?.id
        ];

        for (const candidate of candidates) {
            const normalizedValue = getNormalizedNumericValue(candidate);
            if (normalizedValue !== null) {
                return normalizedValue;
            }
        }

        return null;
    };

    const getCurrentMeterValue = () => (
        vehicleDetail?.odm
        ?? vehicleDetail?.odo_meter
        ?? vehicleDetail?.usage_meter_reading
        ?? vehicleDetail?.usageMeterReading
        ?? 0
    );

    const currentServiceLineItems = getCurrentServiceLineItems();
    const resolvedAssetId = getResolvedAssetId(null);
    const contextVehicleId = getContextVehicleId();
    const contextVehicleTypeId = getContextVehicleTypeId();
    const canLoadPartsList = Boolean(vehicleDetail) && resolvedAssetId !== null;
    const canViewServiceReminders = contextVehicleId !== null;

    useEffect(() => {
        if (!vehicleDetail) {
            return;
        }

        dispatch(fetchReasonsDropdown());
        dispatch(fetchCategoriesDropdown());

        if (canLoadPartsList) {
            dispatch(fetchPartsList({
                assetType: normalizedAssetType,
                assetId: resolvedAssetId,
                ...(Number.isFinite(contextVehicleTypeId) && contextVehicleTypeId > 0
                    ? { vehicleTypeId: contextVehicleTypeId }
                    : {}),
                ...(Number.isFinite(contextVehicleId) && contextVehicleId > 0
                    ? { vehicleId: contextVehicleId }
                    : {})
            }));
        }
    }, [dispatch, vehicleDetail, canLoadPartsList, contextVehicleId, contextVehicleTypeId, normalizedAssetType, resolvedAssetId]);

    useEffect(() => {
        const selectedPartId = getPartIdForWarehouseApi(selectServiceItem);
        if (!selectedPartId) {
            dispatch(clearWarehousesWithPartStock());
            return;
        }
        dispatch(getWarehousesWithPartStock(selectedPartId));
    }, [dispatch, selectServiceItem]);

    useEffect(() => {
        if (canViewServiceReminders) {
            dispatch(fetchServicesReminder({
                page: currentPage,
                limit: 5,
                vehicle_id: contextVehicleId
            }));
        }
    }, [currentPage, canViewServiceReminders, contextVehicleId, dispatch]);

    useEffect(() => {
        const parsedRouteId = getNormalizedNumericValue(id);
        if (parsedRouteId !== null) {
            setReservedServiceId(parsedRouteId);
            return;
        }

        setReservedServiceId(null);
    }, [id]);

    useEffect(() => {
        if (!id) {
            setReservedServiceId(null);
        }
    }, [id, assetType, assetId]);

    useEffect(() => {
        setShowAddItemBtn(false);
        setSelectServiceItem(null);
        setEditLineItem(null);
        setShowServiceReminder(false);
        setCurrentPage(1);
        dispatch(clearWarehousesWithPartStock());
    }, [dispatch, assetType, assetId]);

    useEffect(() => {
        const normalizedNextServiceId = getNormalizedNumericValue(nextServiceId);
        if (!id && normalizedNextServiceId !== null) {
            setReservedServiceId(normalizedNextServiceId);
        }
    }, [id, nextServiceId]);

    const normalizedPartsData = Array.isArray(partsData)
        ? partsData
        : Array.isArray(partsData?.results)
            ? partsData.results
            : Array.isArray(partsData?.data)
                ? partsData.data
                : [];

    const serviceLineItemsOptions = canLoadPartsList ? (normalizedPartsData.map((part) => {
        const stockCount = Number(part?.stock_count ?? part?.part_stock?.available_qty ?? 0);
        const vendorName = part?.vender?.name || part?.vendor?.name || '';
        const manufacturerWithVendor = [part?.manufacturer?.name, vendorName].filter(Boolean).join(' | ');
        return {
            value: part,
            product: part.part_number || '-',
            description: getPartDisplayName(part),
            brand: Number(part?.price ?? 0).toFixed(2),
            priceSource: `Stock: ${stockCount}`,
            manufacturer: manufacturerWithVendor,
            label: getPartDisplayName(part)
        };
    }) || []) : []

    const categoriesOptions = categoriesData?.map((category) => ({
        value: category.value,
        label: category.title
    })) || []

    const reasonsOptions = reasonsData?.map((reason) => ({
        value: reason.value,
        label: reason.title
    })) || []

    const warehouseOptions = (warehousesWithPartStockData || []).map((warehouse) => {
        const qty = Number(warehouse?.parts_stock?.available_qty ?? 0);
        const warehouseName = warehouse?.label || warehouse?.name || 'Unknown Warehouse';
        return {
            value: warehouse?.value ?? warehouse?.id ?? '',
            label: `${warehouseName} (Stock: ${qty})`
        };
    });

    const getSelectedWarehouseOption = (warehouseId, itemContext) => {
        const matched = warehouseOptions.find(option => String(option.value) === String(warehouseId));
        if (matched) return matched;
        if (!warehouseId) return null;

        return {
            value: warehouseId,
            label: getWarehouseLabelFromItem(itemContext) || `Warehouse (${warehouseId})`
        };
    };

    const initialValues = {
        vehicle_id: getResolvedAssetId(null) || '',
        part_id: getPartIdForWarehouseApi(selectServiceItem) || '',
        service_issue_id: 0,
        service_category_id: selectServiceItem?.maintenance_category_id || '',
        cost_parts: selectServiceItem?.price || '',
        rate: selectServiceItem?.price || '',
        qty: '',
        cost_labor: '',
        sub_total: '',
        odo_meter: getCurrentMeterValue(),
        repair_reason_id: '',
        warehouse_id: getWarehouseIdFromItem(selectServiceItem),
        description: ''
    };

    const calculateSubTotalWithQty = (rate, qty, labor) => {
        const rateValue = parseFloat(rate) || 0;
        const qtyValue = parseInt(qty) || 0;
        const laborValue = parseFloat(labor) || 0;
        return ((rateValue * qtyValue) + laborValue).toFixed(2);
    };

    const resolveServiceId = async () => {
        const existingServiceId = getExistingServiceId(editLineItem);
        if (existingServiceId !== null) {
            if (reservedServiceId !== existingServiceId) {
                setReservedServiceId(existingServiceId);
            }
            return existingServiceId;
        }

        if (reservedServiceId !== null) {
            return reservedServiceId;
        }

        const prefetchedServiceId = getNormalizedNumericValue(nextServiceId);
        if (prefetchedServiceId !== null) {
            setReservedServiceId(prefetchedServiceId);
            return prefetchedServiceId;
        }

        const response = await dispatch(fetchNextServiceId()).unwrap();
        const fetchedServiceId = getNormalizedNumericValue(response?.data ?? response);

        if (fetchedServiceId === null) {
            throw new Error('Failed to fetch next service id');
        }

        setReservedServiceId(fetchedServiceId);
        return fetchedServiceId;
    };

    const handleServiceLineSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const parsedWarehouseId = Number(values.warehouse_id);
            const finalWarehouseId = Number.isFinite(parsedWarehouseId) && parsedWarehouseId > 0
                ? parsedWarehouseId
                : null;
            const currentServiceId = await resolveServiceId();
            const currentAssetId = getResolvedAssetId(values.vehicle_id);
            const currentAssetType = normalizedAssetType || 'vehicle';

            const lineItemData = {
                ...(currentAssetType ? { asset_type: currentAssetType } : {}),
                ...(currentAssetId !== null ? { asset_id: currentAssetId } : {}),
                part_id: parseInt(values.part_id),
                service_id: currentServiceId,
                service_issue_id: parseInt(values.service_issue_id),
                service_category_id: parseInt(values.service_category_id),
                cost_parts: parseFloat(values.cost_parts),
                rate: parseFloat(values.rate),
                qty: parseInt(values.qty),
                cost_labor: parseFloat(values.cost_labor),
                sub_total: parseFloat(values.sub_total),
                odo_meter: parseInt(values.odo_meter),
                repair_reason_id: parseInt(values.repair_reason_id),
                warehouse_id: finalWarehouseId,
                description: values.description
            };

            if (editLineItem) {
                await dispatch(updateServiceLineItem({ service_line_item_id: editLineItem.id, ...lineItemData })).unwrap();
                successNotify('Service line item updated successfully');
            } else {
                await dispatch(createServiceLineItem(lineItemData)).unwrap();
                successNotify('Service line item added successfully');
            }
            resetForm();
            setSelectServiceItem(null);
            setShowAddItemBtn(false);
            setEditLineItem(null);

            // Use shared refresh function instead of direct API call
            if (refreshVehicleDetail) {
                await refreshVehicleDetail(currentServiceId);
            }
        } catch (error) {
            errorNotify(error?.message || error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleServiceItemSelect = (selectedOption) => {
        editLineItem && setEditLineItem(null);
        setSelectServiceItem(selectedOption?.value || null);
    };

    const serviceLineReminder = <CustomModal heading="Service Reminders" size="lg" show={showServiceReminder} handleClose={() => setShowServiceReminder(false)}>
        <div className='service_line_items_main' style={{ border: "none", padding: "0px" }}>
            {
                getServicesReminderLoading ? <div className='no_vehicle_selected'>
                    <p>Loading...</p>
                </div> :
                    <div className='service_line_items_table' style={{ borderRadius: "0px" }}>
                        {
                            getServicesReminderPaginated?.results?.length === 0 ?
                                <div className='no_vehicle_selected'>
                                    <p>No service reminders found.</p>
                                </div> :
                                <>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th>Service Schedule</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                getServicesReminderPaginated?.results?.map((item, index) => {
                                                    const serviceItems = item?.service_line_items || [];

                                                    const firstItem = serviceItems[0]?.reason?.title || 'N/A';
                                                    const remainingCount = serviceItems.length - 1;

                                                    return (
                                                        <tr key={index + 1}>
                                                            <td>
                                                                <div className="service-tasks-container" style={{ position: 'relative' }}>
                                                                    <span className="first-task">{firstItem}</span>
                                                                    {
                                                                        remainingCount > 0 && (
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip>
                                                                                        <div>
                                                                                            {serviceItems.slice(1).map((item, idx) => (
                                                                                                <div key={idx} className='tooltip_item'>{item?.reason?.title}</div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="additional-tasks">+{remainingCount}</span>
                                                                            </OverlayTrigger>

                                                                        )
                                                                    }
                                                                </div>
                                                            </td>
                                                            <td>{item?.start_at ? dateMonthYear(item?.start_at) : ''}</td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                    <PaginationCustom
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        totalItems={getServicesReminderPaginated?.meta?.totalItems}
                                        totalPages={getServicesReminderPaginated?.meta?.totalPages}
                                        showLimit={false}
                                        className="service_line_items_pagination"
                                    />
                                </>
                        }

                    </div>
            }
        </div>
    </CustomModal>

    const handleDeleteServiceLineItem = async (id) => {
        try {
            await dispatch(deleteServiceLineItem(id)).unwrap();
            successNotify('Service Line Item deleted successfully');
            // Use shared refresh function instead of callApi
            if (refreshVehicleDetail) {
                await refreshVehicleDetail(reservedServiceId);
            }
        } catch (error) {
            errorNotify(error);
        }
    }

    const handleEditLineItem = (item) => {
        setEditLineItem(item);
        setSelectServiceItem(item);
        setShowAddItemBtn(true);
    };

    return (
        <div className='service_line_items_main'>
            {serviceLineReminder}
            <div className='d-flex justify-content-between align-items-center'>
                <h6>Service Line Items</h6>

                <button disabled={!canViewServiceReminders || vehicleDetailLoading} type='button' className='reminder_service_btn' onClick={() => setShowServiceReminder(true)}>{serviceLineItemIcon} View Service Reminders</button>
            </div>
            {
                !vehicleDetail ?
                    <div className='no_vehicle_selected'>
                        {
                            vehicleDetailLoading ? <p>Loading...</p> :
                                <p>No {displayAssetLabel.toLowerCase()} selected. Please select a {displayAssetLabel.toLowerCase()} to track service line items.</p>
                        }
                    </div> :
                    vehicleDetailLoading ?
                        <div className='no_vehicle_selected'>
                            <p>Loading...</p>
                        </div> :
                        <>
                            <div className='service_line_items_table'>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Description</th>
                                            <th>Parts (PKR)</th>
                                            <th>Labor (PKR)</th>
                                            <th>Subtotal (PKR)</th>
                                            <th>Notes</th>
                                            <th>Categorizations</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            currentServiceLineItems.length > 0 ? currentServiceLineItems.map((item, index) => {
                                                return (
                                                    <tr key={index + 1}>
                                                        <td>{getLineItemDisplayName(item)}</td>
                                                        <td>{item?.cost_parts ?? item?.rate}</td>
                                                        <td>{item?.cost_labor}</td>
                                                        <td>{item?.sub_total}</td>
                                                        <td>
                                                            <div className="two-line-ellipsis">
                                                                <MultiLineText text={item?.description} maxWidth={"70px"} />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="two-line-ellipsis">
                                                                <MultiLineText span={true} text={item?.category?.title} maxWidth={"110px"} />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <MdEdit className='edit_line_item_icon' onClick={() => handleEditLineItem(item)} />
                                                                <MdDeleteOutline className='delete_line_item_icon' onClick={() => handleDeleteServiceLineItem(item?.id)} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            }) : (
                                                <tr>
                                                    <td colSpan={8}>
                                                        No service line items found for this {displayAssetLabel.toLowerCase()}.
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </Table>
                                {
                                    showAddItemBtn &&
                                    <>
                                        {
                                            selectServiceItem &&
                                            <Formik
                                                initialValues={editLineItem ? {
                                                    vehicle_id: getResolvedAssetId(null) || '',
                                                    part_id: editLineItem?.part_id || '',
                                                    service_issue_id: editLineItem?.service_issue_id || 0,
                                                    service_category_id: editLineItem?.service_category_id || '',
                                                    cost_parts: editLineItem?.cost_parts || '',
                                                    rate: editLineItem?.rate || '',
                                                    qty: editLineItem?.qty || '',
                                                    cost_labor: editLineItem?.cost_labor || '',
                                                    sub_total: editLineItem?.sub_total || '',
                                                    odo_meter: editLineItem?.odo_meter || getCurrentMeterValue(),
                                                    repair_reason_id: editLineItem?.repair_reason_id || '',
                                                    warehouse_id: getWarehouseIdFromItem(editLineItem),
                                                    description: editLineItem?.description || ''
                                                } : initialValues}
                                                validationSchema={serviceLineSchema}
                                                onSubmit={handleServiceLineSubmit}
                                                enableReinitialize={true}
                                            >
                                                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => (
                                                    <div className='service_line_form'>
                                                        <div>
                                                            <div className='service_line_item_header'>
                                                                <h5 style={{ maxWidth: "120px" }}>{editLineItem ? getLineItemDisplayName(editLineItem) : getLineItemDisplayName(selectServiceItem)}</h5>

                                                                <div className='d-flex gap-2'>
                                                                    {/* <Input
                                                                    label="Parts (PKR)"
                                                                    type="hidden"
                                                                    name="cost_parts"
                                                                    value={values.cost_parts}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        setFieldValue('sub_total', calculateSubTotal(e.target.value, values.cost_labor));
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    className='service_entry_price'
                                                                    error={errors.cost_parts}
                                                                    touched={touched.cost_parts}

                                                                /> */}
                                                                    <Input
                                                                        label="Unit Price (PKR)"
                                                                        type="number"
                                                                        name="rate"
                                                                        value={values.rate}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            const newRate = parseFloat(e.target.value) || 0;
                                                                            const qty = parseInt(values.qty) || 0;
                                                                            const costParts = newRate * qty;
                                                                            // setFieldValue('cost_parts', costParts);
                                                                            setFieldValue('sub_total', calculateSubTotalWithQty(e.target.value, values.qty, values.cost_labor));
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        className='service_entry_price'
                                                                        error={errors.rate}
                                                                        touched={touched.rate}
                                                                    />
                                                                    <Input
                                                                        label="Quantity"
                                                                        type="number"
                                                                        name="qty"
                                                                        value={values.qty}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            const newQty = parseInt(e.target.value) || 0;
                                                                            const rate = parseFloat(values.rate) || 0;
                                                                            const costParts = rate * newQty;
                                                                            setFieldValue('cost_parts', costParts);
                                                                            setFieldValue('sub_total', calculateSubTotalWithQty(values.rate, e.target.value, values.cost_labor));
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        className='service_entry_price'
                                                                        error={errors.qty}
                                                                        touched={touched.qty}
                                                                    />
                                                                    <Input
                                                                        label="Labor (PKR)"
                                                                        type="number"
                                                                        name="cost_labor"
                                                                        value={values.cost_labor}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            setFieldValue('sub_total', calculateSubTotalWithQty(values.rate, values.qty, e.target.value));
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        className='service_entry_price'
                                                                        error={errors.cost_labor}
                                                                        touched={touched.cost_labor}
                                                                    />
                                                                    <Input
                                                                        label="Subtotal (PKR)"
                                                                        type="number"
                                                                        name="sub_total"
                                                                        value={values.sub_total}
                                                                        className='service_entry_price'
                                                                        error={errors.sub_total}
                                                                        touched={touched.sub_total}
                                                                    />
                                                                </div>

                                                                <div className='save_button_container d-flex align-items-center' style={{ paddingRight: "3px", gap: "2px" }}>
                                                                    <button
                                                                        className="save_line_item_btn"
                                                                        type="button"
                                                                        disabled={createLineItemLoading}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            submitForm();
                                                                        }}>
                                                                        {saveLineItemIcon}
                                                                    </button>
                                                                    <MdDeleteOutline style={{ fontSize: "22px" }} className='delete_line_item_icon' onClick={() => { setSelectServiceItem(null); setEditLineItem(null); }} />
                                                                </div>
                                                            </div>
                                                            <div className='service_line_item_details'>
                                                                <p>{lastCompleteIcon} <span>Last Completed:</span> {selectServiceItem?.part_service_line_item ? dateMonthYear(selectServiceItem?.part_service_line_item?.service_short_info?.complete_at) : '-'} . {selectServiceItem?.part_service_line_item ? selectServiceItem?.part_service_line_item?.odo_meter : '-'} km ago</p>
                                                                <p>{nextDueIcon} <span>Next Due:</span> 3 months from now</p>
                                                            </div>

                                                            <textarea
                                                                rows={3}
                                                                placeholder='Add notes or additional details'
                                                                name="description"
                                                                value={values.description}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                            />
                                                            {touched.description && errors.description && (
                                                                <div className="error-message" style={{ color: '#ff0000de', fontSize: '12px', marginTop: "3px" }}>
                                                                    {errors.description}
                                                                </div>
                                                            )}

                                                            <div className='service_line_item_categorization'>
                                                                <h6>Maintenance Categorization</h6>

                                                                <div>
                                                                    <Row>
                                                                        <Col md={4}>
                                                                            <Select
                                                                                label="Category"
                                                                                placeholder="Select Category"
                                                                                options={categoriesOptions}
                                                                                loading={categoriesLoading}
                                                                                name="service_category_id"
                                                                                value={categoriesOptions.find(option => option.value === values.service_category_id) || null}
                                                                                onChange={(selectedOption) => setFieldValue('service_category_id', selectedOption?.value || '')}
                                                                                onBlur={handleBlur}
                                                                                error={errors.service_category_id}
                                                                                touched={touched.service_category_id}
                                                                                isSearch={true}
                                                                            />
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <Select
                                                                                label="Reason for Repair"
                                                                                placeholder="Select Reason for Repair"
                                                                                options={reasonsOptions}
                                                                                loading={reasonsLoading}
                                                                                name="repair_reason_id"
                                                                                value={reasonsOptions.find(option => option.value === values.repair_reason_id) || null}
                                                                                onChange={(selectedOption) => setFieldValue('repair_reason_id', selectedOption?.value || '')}
                                                                                onBlur={handleBlur}
                                                                                error={errors.repair_reason_id}
                                                                                touched={touched.repair_reason_id}
                                                                                isSearch={true}
                                                                            />
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <Select
                                                                                label="Warehouse"
                                                                                placeholder="Select Warehouse"
                                                                                options={warehouseOptions}
                                                                                loading={getWarehousesWithPartStockLoading}
                                                                                name="warehouse_id"
                                                                                value={getSelectedWarehouseOption(values.warehouse_id, editLineItem || selectServiceItem)}
                                                                                onChange={(selectedOption) => setFieldValue('warehouse_id', selectedOption?.value || '')}
                                                                                onBlur={handleBlur}
                                                                                isSearch={true}
                                                                            />
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Formik>
                                        }
                                        {
                                            !editLineItem &&
                                            <div className='service_line_items_search'>
                                                <Select
                                                    label=""
                                                    placeholder="Search and Select Item"
                                                    options={serviceLineItemsOptions}
                                                    loading={canLoadPartsList && partsLoading}
                                                    name=""
                                                    value={serviceLineItemsOptions.find(option => option.value?.id === selectServiceItem?.id) || null}
                                                    onChange={handleServiceItemSelect}
                                                    showIcon={true}
                                                    icon={searchIcon}
                                                    isSearch={true}
                                                />
                                            </div>
                                        }
                                    </>
                                }
                            </div>
                            <div>
                                {
                                    !editLineItem &&
                                    <button
                                        disabled={!vehicleDetail || vehicleDetailLoading}
                                        className='add_issue_btn'
                                        type="button"
                                        onClick={() => setShowAddItemBtn(!showAddItemBtn)}>+ Add Line Item</button>
                                }
                            </div>
                        </>
            }
        </div>
    )
}

export default ServiceLineItem
